import React, {useMemo} from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {DocumentDownload} from '@carbon/icons-react';
import {WrapperFlex, ScrollableFlex} from '../../utils/scrolling';
import {Controller, useForm} from 'react-hook-form';
import Select from 'react-select';
import {useGetTestLists} from '../../../hooks/api/testList';
import {useTask} from '../../../hooks/api/tasks';
import {useMarketFromContext} from '../MarketProvider';
import {
  useGenerateMarketReport,
  useGetMarketReports,
  useGetMarketReport,
  MarketReportQueryKeys,
} from '../../Debug/hooks/marketReports';
import {useQueryClient} from '@tanstack/react-query';
import {MarketReportMinCustomerSampleSize} from '../../../shared/api/definitions/debug';
import {TestListLabel} from '../../Settings/TestLists/TestListLabel';
import {WithId} from '../../../shared/util';
import {TestList} from '../../../shared/testLists';
import {lightFormat} from 'date-fns';
import FileSaver from 'file-saver';
import {useState, useEffect} from 'react';
import {useCustomer} from '../../../hooks/api/metadata';
import {ObjectId} from 'bson';
import {Parser} from '@json2csv/plainjs';

const InsightsSection = ({children, ...props}: React.PropsWithChildren<{}>) => (
  <Box
    px={6}
    pt={6}
    pb={6}
    bgColor="kgray.50"
    borderColor="kgray.200"
    borderWidth="1px"
    borderRadius="lg"
    width="100%"
    {...props}
  >
    {children}
  </Box>
);

const downloadMarketReportFile = ({
  content,
  filename,
  type,
}: {
  content: string;
  filename: string;
  type: 'text/csv' | 'text/markdown';
}) => {
  const ext = type === 'text/csv' ? 'csv' : 'md';
  const blob = new Blob([content], {type});
  FileSaver.saveAs(blob, `${filename}.${ext}`);
};

type TestListOption = {
  testList: WithId<TestList>;
  accountsText: string;
  isDisabled: boolean;
};

type MarketReportForm = {
  domain: string;
  testList: TestListOption;
};

export const Insights = () => {
  const market = useMarketFromContext();
  const testLists = useGetTestLists({});
  const generateMarketReport = useGenerateMarketReport();
  const marketReports = useGetMarketReports({marketId: market.id});
  const queryClient = useQueryClient();
  const customer = useCustomer();

  const [downloadingReportId, setDownloadingReportId] =
    useState<ObjectId | null>(null);

  const {
    data: marketReportData,
    isFetching: isMarketReportFetching,
    isSuccess: isMarketReportSuccess,
  } = useGetMarketReport(
    downloadingReportId ? {reportId: downloadingReportId} : 'skipToken'
  );

  useEffect(() => {
    if (
      !downloadingReportId ||
      isMarketReportFetching ||
      !isMarketReportSuccess ||
      !marketReportData
    ) {
      return;
    }

    const reportInfo = marketReports.data?.find((r) =>
      r._id.equals(downloadingReportId)
    );
    const reportName = reportInfo
      ? `${customer.name}-${market.label}-${reportInfo.domain}-${lightFormat(
          reportInfo.timestamp,
          'yyyy-MM-dd'
        )}`
      : `${customer.name}-${market.label}-report`;

    const customerAccountTiersCsv = new Parser().parse(
      marketReportData.customerAccountTiers.map((result) => {
        return {
          Domain: result.domain,
          'Fit Score': result.fitScore,
          Tier: result.tier,
          'Tier Reasoning': result.tierReasoning,
          Segment: result.segment,
          'Segment Reasoning': result.segmentReasoning,
        };
      })
    );

    downloadMarketReportFile({
      content: customerAccountTiersCsv,
      filename: `${reportName}-customerAccountTiers`,
      type: 'text/csv',
    });

    downloadMarketReportFile({
      content: marketReportData.prompts,
      filename: `${reportName}-prompts`,
      type: 'text/markdown',
    });

    downloadMarketReportFile({
      content: marketReportData.report,
      filename: `${reportName}-report`,
      type: 'text/markdown',
    });

    setDownloadingReportId(null);
  }, [
    downloadingReportId,
    isMarketReportFetching,
    isMarketReportSuccess,
    marketReportData,
    marketReports.data,
    customer.name,
    market.label,
  ]);

  const {isTaskInProgress} = useTask(
    {
      type: 'generateMarketReport',
      marketId: market.id,
    },
    {
      onTaskComplete: async (task) => {
        if (task.state === 'done') {
          // Invalidate the market reports list to refetch the new report
          await queryClient.invalidateQueries({
            queryKey: MarketReportQueryKeys.market(market.id.toString()),
          });
        }
      },
    }
  );

  const testListOptions = useMemo(
    () =>
      (testLists.data ?? []).map((testList) => {
        if (testList.status.type === 'processing') {
          return {
            testList,
            accountsText: 'Processing',
            isDisabled: true,
          };
        }

        const numAccounts = testList.status.numAccounts;
        const isTooSmall = numAccounts < MarketReportMinCustomerSampleSize;

        return {
          testList,
          accountsText: `${numAccounts.toLocaleString()} accounts`,
          isDisabled: isTooSmall,
        };
      }),
    [testLists.data]
  );

  const {
    control,
    register,
    handleSubmit,
    formState: {errors},
  } = useForm<MarketReportForm>();

  const formatOptionLabel = (option: TestListOption) => (
    <Flex alignItems="center" justifyContent="space-between" w="100%">
      <TestListLabel testList={option.testList} />
      <Text color="kgray.300" fontSize="sm">
        ({option.accountsText})
      </Text>
    </Flex>
  );

  const onGenerateReport = handleSubmit((data) => {
    const {domain, testList} = data;
    if (!testList || !domain) {
      return;
    }

    generateMarketReport.mutate({
      domain,
      testListId: testList.testList._id,
      marketId: market.id,
    });
  });

  const isGenerating = isTaskInProgress || generateMarketReport.isPending;

  return (
    <WrapperFlex>
      <ScrollableFlex p={0}>
        <Center>
          <Box minWidth="600px" width="50%" py={6}>
            <VStack gap="3">
              <InsightsSection>
                <Text fontSize="2xl">Generate Market Report</Text>
                <Tooltip
                  label="A market report is currently being generated"
                  isDisabled={!isGenerating}
                >
                  <Box as="form" onSubmit={onGenerateReport} w="100%" mt="4">
                    <Flex direction="column" gap="6">
                      <FormControl isInvalid={!!errors.testList}>
                        <FormLabel>
                          Select a test list for market analysis
                        </FormLabel>
                        <Controller
                          name="testList"
                          control={control}
                          rules={{required: true}}
                          render={({field, fieldState: {error}}) => (
                            <Select
                              {...field}
                              isLoading={testLists.isPending}
                              options={testListOptions}
                              formatOptionLabel={formatOptionLabel}
                              getOptionLabel={(option) => option.testList.label}
                              getOptionValue={(option) =>
                                option.testList._id.toString()
                              }
                              isOptionDisabled={(option) => option.isDisabled}
                              isDisabled={isGenerating}
                              styles={{
                                control: (base) => ({
                                  ...base,
                                  borderColor: error ? 'red' : base.borderColor,
                                  boxShadow: error
                                    ? '0 0 0 1px red'
                                    : base.boxShadow,
                                  '&:hover': {
                                    borderColor: error
                                      ? 'red'
                                      : base.borderColor,
                                  },
                                }),
                              }}
                            />
                          )}
                        />
                        <FormHelperText>
                          Minimum test list size is{' '}
                          {MarketReportMinCustomerSampleSize} accounts.
                        </FormHelperText>
                      </FormControl>

                      <FormControl isInvalid={!!errors.domain}>
                        <FormLabel>Enter your website domain</FormLabel>
                        <Input
                          autoComplete="off"
                          bgColor="white"
                          placeholder="e.g. example.com"
                          isDisabled={isGenerating}
                          {...register('domain', {
                            required: true,
                          })}
                        />
                      </FormControl>

                      <Button
                        colorScheme="kbuttonblue"
                        type="submit"
                        w="fit-content"
                        isLoading={generateMarketReport.isPending}
                        isDisabled={isGenerating}
                      >
                        Generate Market Report
                      </Button>
                    </Flex>
                  </Box>
                </Tooltip>
              </InsightsSection>

              <InsightsSection>
                <Text fontSize="2xl">Reports</Text>
                {marketReports.data && marketReports.data.length > 0 ? (
                  <TableContainer w="100%" mt="4">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Date</Th>
                          <Th>Test List</Th>
                          <Th>Domain</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {marketReports.data.map((report) => {
                          const testList = testLists.data?.find((tl) =>
                            tl._id.equals(report.testListId)
                          );
                          const reportId = report._id;
                          const isDownloading =
                            downloadingReportId?.equals(reportId);

                          return (
                            <Tr key={reportId.toString()}>
                              <Td>
                                {lightFormat(
                                  report.timestamp,
                                  'M/dd/yy h:mm a'
                                )}
                              </Td>
                              <Td>
                                {testList ? (
                                  <TestListLabel testList={testList} />
                                ) : (
                                  'Unknown'
                                )}
                              </Td>
                              <Td>{report.domain}</Td>
                              <Td>
                                <Tooltip label="Download CSV files">
                                  <IconButton
                                    aria-label="Download report"
                                    icon={<DocumentDownload />}
                                    size="sm"
                                    variant="ghost"
                                    isLoading={isDownloading}
                                    onClick={() =>
                                      setDownloadingReportId(reportId)
                                    }
                                  />
                                </Tooltip>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Text color="kgray.300" textAlign="center" py="4">
                    No reports generated yet
                  </Text>
                )}
              </InsightsSection>
            </VStack>
          </Box>
        </Center>
      </ScrollableFlex>
    </WrapperFlex>
  );
};
